import { useCallback, useEffect, useRef, useState } from 'react';
import Api from '../../Services/Api';
import { getLanguage } from '../../Services/i18n';
import { asArray, normalizeActions, normalizePreview } from './contracts';

export default function usePropertyChatSocket({ t, setMessages, setPendingActions }) {
    const [isTyping, setIsTyping] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [applyFailed, setApplyFailed] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const applyTimeoutRef = useRef(null);
    const lastAppliedActionsRef = useRef([]);

    useEffect(() => {
        const socket = Api.socket;

        const handleResponse = (data) => {
            setIsTyping(false);
            const safeData = (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
            const normalizedActions = normalizeActions(safeData.actions);
            const normalizedPreview = normalizePreview(safeData.preview);
            const msg = {
                role: 'assistant',
                content: typeof safeData.message === 'string' ? safeData.message : t('configFailed'),
                preview: normalizedPreview,
                actions: normalizedActions,
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, msg]);
            if (normalizedActions.length > 0) {
                setPendingActions(normalizedActions);
                lastAppliedActionsRef.current = normalizedActions;
                setApplyFailed(false);
            }
        };

        const handleTyping = (typing) => {
            setIsTyping(!!typing);
        };

        const handleApplied = (data) => {
            clearTimeout(applyTimeoutRef.current);
            setIsApplying(false);
            setPendingActions(null);
            const results = asArray(data);
            const allSuccess = results.length > 0 && results.every(r => r && r.success);
            setSnackbar({
                open: true,
                message: allSuccess ? t('configApplied') : t('configFailed'),
                severity: allSuccess ? 'success' : 'error'
            });
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: allSuccess
                    ? `${t('configApplied')} (${results.map(r => `${t(r.objectType)}: ${r.count || 0}`).join(', ')})`
                    : t('configFailed'),
                timestamp: Date.now()
            }]);
        };

        const handleError = (error) => {
            setIsTyping(false);
            clearTimeout(applyTimeoutRef.current);
            setIsApplying(false);
            setApplyFailed(true);
            const message = typeof error?.message === 'string' ? error.message : t('configFailed');
            setSnackbar({ open: true, message, severity: 'error' });
        };

        socket.on('propertyChatResponse', handleResponse);
        socket.on('propertyChatTyping', handleTyping);
        socket.on('propertyChatApplied', handleApplied);
        socket.on('propertyChatError', handleError);

        return () => {
            clearTimeout(applyTimeoutRef.current);
            socket.off('propertyChatResponse', handleResponse);
            socket.off('propertyChatTyping', handleTyping);
            socket.off('propertyChatApplied', handleApplied);
            socket.off('propertyChatError', handleError);
        };
    }, [setMessages, setPendingActions, t]);

    const sendMessage = useCallback((text) => {
        if (!text.trim() || isTyping) return false;
        const trimmedText = text.trim();
        setMessages(prev => [...prev, { role: 'user', content: trimmedText, timestamp: Date.now() }]);
        setPendingActions(null);
        setApplyFailed(false);
        Api.sendPropertyChatMessage(trimmedText, getLanguage());
        return true;
    }, [isTyping, setMessages, setPendingActions]);

    const applyActions = useCallback((pendingActions) => {
        const safeActions = normalizeActions(pendingActions);
        if (safeActions.length === 0 || isApplying) return false;

        setIsApplying(true);
        setApplyFailed(false);
        Api.applyPropertyChatConfig(safeActions);
        clearTimeout(applyTimeoutRef.current);
        applyTimeoutRef.current = setTimeout(() => {
            setIsApplying(false);
            setApplyFailed(true);
            setSnackbar({ open: true, message: t('configTimeout', 'Request timed out, retry.'), severity: 'error' });
        }, 15000);
        return true;
    }, [isApplying, t]);

    const retryApply = useCallback((pendingActions) => {
        const fallbackActions = normalizeActions(pendingActions);
        const toApply = fallbackActions.length > 0 ? fallbackActions : lastAppliedActionsRef.current;
        return applyActions(toApply);
    }, [applyActions]);

    return {
        isTyping,
        isApplying,
        applyFailed,
        snackbar,
        setSnackbar,
        sendMessage,
        applyActions,
        retryApply,
    };
}

